import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
  type RefObject,
} from "react";

import { AttachmentTile } from "./AttachmentTile";
import {
  DEFAULT_ASPECT_RATIO,
  type AttachmentPreviewKind,
  type GalleryPicture,
} from "./attachmentPreview";
import {
  collapsedHeightBudget,
  layoutJustifiedRows,
  rowsWithinBudget,
  targetRowHeight,
  type GalleryRow,
  type GalleryTileBox,
} from "./justifiedLayout";

/** Space between tiles, in CSS pixels. */
const TILE_GAP_PX = 6;
/** Width assumed before the gallery has been measured (and in jsdom). */
const FALLBACK_WIDTH_PX = 320;
/** Viewport height assumed where there is no window. */
const FALLBACK_VIEWPORT_HEIGHT_PX = 800;

function subscribeToResize(onChange: () => void): () => void {
  window.addEventListener("resize", onChange);
  return () => window.removeEventListener("resize", onChange);
}

/** The viewport height in CSS pixels — browser zoom changes it, which is how
 *  the gallery follows zoom. */
function useViewportHeight(): number {
  return useSyncExternalStore(
    subscribeToResize,
    () => window.innerHeight,
    () => FALLBACK_VIEWPORT_HEIGHT_PX
  );
}

/** The element's content width, live; null until measured. */
function useMeasuredWidth<T extends HTMLElement>(): [RefObject<T>, number | null] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver((entries) => {
      setWidth(entries[0]?.contentRect.width ?? null);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

export interface GalleryAttachment extends GalleryPicture {
  kind: AttachmentPreviewKind;
  onOpen?: () => void;
  processingLabel?: string | null;
  action?: ReactNode;
}

interface AttachmentGalleryProps {
  attachments: GalleryAttachment[];
  buildingId: number;
  onPreviewFailed: (key: string) => void;
  /** "+N" tile's accessible label; `{count}` is replaced. */
  showAllLabel: string;
  showFewerLabel: string;
}

function placeTiles(rows: GalleryRow[]) {
  const placed: Array<{ tile: GalleryTileBox; top: number; left: number; height: number }> = [];
  let top = 0;
  rows.forEach((row, rowIndex) => {
    if (rowIndex > 0) top += TILE_GAP_PX;
    let left = 0;
    row.tiles.forEach((tile, tileIndex) => {
      if (tileIndex > 0) left += TILE_GAP_PX;
      placed.push({ tile, top, left, height: row.height });
      left += tile.width;
    });
    top += row.height;
  });
  return { placed, height: top };
}

/**
 * The issue panel's visual attachments — images and PDFs shown as themselves.
 * Tiles keep their aspect ratio and pack into justified rows that span the
 * panel (see `justifiedLayout`), so a few attachments sit side by side rather
 * than stacking into a long column. Row height follows the viewport height,
 * which browser zoom changes, and shrinks as attachments are added; past
 * roughly half the viewport the rest fold behind a "+N" tile.
 */
export function AttachmentGallery({
  attachments,
  buildingId,
  onPreviewFailed,
  showAllLabel,
  showFewerLabel,
}: AttachmentGalleryProps) {
  const [containerRef, measuredWidth] = useMeasuredWidth<HTMLDivElement>();
  const viewportHeight = useViewportHeight();
  const [aspectRatios, setAspectRatios] = useState<ReadonlyMap<string, number>>(new Map());
  const [expanded, setExpanded] = useState(false);

  const handleAspectRatio = useCallback((key: string, ratio: number) => {
    setAspectRatios((current) => {
      // Ignore sub-pixel noise, so a re-render with the same picture cannot
      // relayout the gallery.
      const known = current.get(key);
      if (known != null && Math.abs(known - ratio) < 0.005) return current;
      return new Map(current).set(key, ratio);
    });
  }, []);

  const containerWidth = measuredWidth ?? FALLBACK_WIDTH_PX;
  const targetHeight = targetRowHeight(viewportHeight, attachments.length);

  const rows = useMemo(
    () =>
      layoutJustifiedRows(
        attachments.map(({ key, kind }) => aspectRatios.get(key) ?? DEFAULT_ASPECT_RATIO[kind]),
        { containerWidth, targetHeight, gap: TILE_GAP_PX }
      ),
    [attachments, aspectRatios, containerWidth, targetHeight]
  );

  const rowsThatFit = rowsWithinBudget(
    rows,
    collapsedHeightBudget(viewportHeight, targetHeight),
    TILE_GAP_PX
  );
  const collapsible = rowsThatFit < rows.length;
  const visibleRows = expanded || !collapsible ? rows : rows.slice(0, rowsThatFit);
  const hiddenCount = rows
    .slice(visibleRows.length)
    .reduce((sum, row) => sum + row.tiles.length, 0);

  // One flat list of absolutely placed tiles keyed by file, so a tile that
  // moves to another row as aspect ratios arrive keeps its loaded picture
  // instead of remounting (and re-drawing a PDF).
  const { placed, height } = placeTiles(visibleRows);
  const lastShown = placed.at(-1)?.tile.index;

  return (
    <div ref={containerRef} className="mt-1.5 w-full">
      <ul className="relative" style={{ height }}>
        {placed.map(({ tile, top: tileTop, left, height }) => {
          const attachment = attachments[tile.index];
          return (
            <AttachmentTile
              key={attachment.key}
              picture={attachment}
              kind={attachment.kind}
              buildingId={buildingId}
              style={{ position: "absolute", top: tileTop, left, width: tile.width, height }}
              renderWidth={tile.width}
              onOpen={attachment.onOpen}
              processingLabel={attachment.processingLabel}
              action={attachment.action}
              onAspectRatio={handleAspectRatio}
              onPreviewFailed={onPreviewFailed}
              hiddenCount={tile.index === lastShown ? hiddenCount : 0}
              onShowHidden={() => setExpanded(true)}
              showHiddenLabel={showAllLabel.replace("{count}", String(attachments.length))}
            />
          );
        })}
      </ul>
      {expanded && collapsible && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="mt-1 text-xs text-muted-foreground hover:text-foreground hover:underline focus:underline focus:outline-none"
        >
          {showFewerLabel}
        </button>
      )}
    </div>
  );
}
